import ISkillAlias from "../../models/skillAlias";
import {
  createFeatureSelector,
  createSelector,
  on,
  createReducer,
} from "@ngrx/store";
import { ISkillNode } from "../../models/skillNode";
import {
  SkillAliasPageActions,
  SkillAliasApiActions,
  AddSkillAliasActions,
} from "../actions";

export interface ISkillAliasesState {
  skillAliases: ISkillAlias[];
  isLoading: ISkillAliasesLoading;
  selectedParentSkill: ISkillNode | undefined;
  temporarySkillAliases: ISkillNode[];
}

export interface ISkillAliasesLoading {
  loadSkillAliases: boolean;
  addSkillAlias: boolean;
  deleteSkillAlias: boolean;
}

const initialState: ISkillAliasesState = {
  skillAliases: [],
  isLoading: {
    loadSkillAliases: false,
    addSkillAlias: false,
    deleteSkillAlias: false,
  },
  selectedParentSkill: undefined,
  temporarySkillAliases: [],
};

export const reducer = createReducer(
  initialState,
  on(SkillAliasPageActions.loadPage, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, loadSkillAliases: true },
  })),
  on(SkillAliasApiActions.populateSkillAliases, (state, action) => ({
    ...state,
    skillAliases: action.skillAliases,
    isLoading: { ...state.isLoading, loadSkillAliases: false },
  })),
  on(AddSkillAliasActions.saveTemporarySkillAliases, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, addSkillAlias: true },
  })),
  on(SkillAliasApiActions.skillAliasesSaved, (state) => ({
    ...state,
    temporarySkillAliases: [],
    selectedParentSkill: undefined,
    isLoading: { ...state.isLoading, addSkillAlias: false },
  })),
  on(SkillAliasPageActions.requestDeleteSkillAlias, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, deleteSkillAlias: true },
  })),
  on(SkillAliasApiActions.skillAliasDeleted, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, deleteSkillAlias: false },
  })),
  on(AddSkillAliasActions.selectParentSkill, (state, action) => {
    const existingSkillAliases = state.skillAliases.find(
      (skillAlias) => skillAlias.Id === action.parentSkill.Id,
    );
    return {
      ...state,
      selectedParentSkill: action.parentSkill,
      temporarySkillAliases: existingSkillAliases
        ? existingSkillAliases.ChildSkills
        : [],
    };
  }),
  on(AddSkillAliasActions.addTemporarySkillAlias, (state, action) => ({
    ...state,
    temporarySkillAliases: [...state.temporarySkillAliases, action.skill],
  })),
  on(AddSkillAliasActions.deleteTemporarySkillAlias, (state, action) => ({
    ...state,
    temporarySkillAliases: [
      ...state.temporarySkillAliases.filter(
        (skillAlias) => skillAlias.Id !== action.skill.Id,
      ),
    ],
  })),
);

export const getSkillAliasState =
  createFeatureSelector<ISkillAliasesState>("skillAliases");
export const getSkillAliases = createSelector(
  getSkillAliasState,
  (skillAliasState) => skillAliasState.skillAliases,
);
export const getSkillAliasLoading = createSelector(
  getSkillAliasState,
  (skillAliasState) => skillAliasState.isLoading,
);
export const getSelectedParentSkill = createSelector(
  getSkillAliasState,
  (skillAliasState) => skillAliasState.selectedParentSkill,
);
export const getSelectedParentSkillChildSkills = createSelector(
  getSelectedParentSkill,
  getSkillAliases,
  (selectedParentSkill, allSkillAliases) => {
    if (!selectedParentSkill) {
      return [];
    }
    const selectedSkillAlias = allSkillAliases.find(
      (skillAlias) => skillAlias.Id === selectedParentSkill.Id,
    );
    return selectedSkillAlias ? selectedSkillAlias.ChildSkills : [];
  },
);
export const getTemporarySkillAliases = createSelector(
  getSkillAliasState,
  (skillAliasState) => skillAliasState.temporarySkillAliases,
);
